// Iter 84 · Phase 7 — Audit Timeline Panel
// Surfaces the existing /api/audit log to tenant admins. Read-only.
import React, { useEffect, useState } from "react";
import { Activity, ChevronRight, User } from "lucide-react";
import { api } from "@/lib/api";

const ICON_FOR = {
  "punch.created": Activity,
  "punch.assigned": User,
  "notification.publish": Activity,
  "escalation.scan": Activity,
  "digest.daily": Activity,
  "share.created": Activity,
  "export.completed": Activity,
  "email.delivered": Activity,
  "email.skipped_no_key": Activity,
  "mention.created": User,
};

export default function AuditTimelinePanel({ targetType, targetId, limit = 50 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/audit", { params: { limit } });
        const events = (Array.isArray(data) ? data : []).filter((r) => {
          if (!targetType) return true;
          return r.target_type === targetType
                 && (!targetId || r.target_id === targetId);
        });
        setRows(events);
      } catch {
        setRows([]);
      } finally { setLoading(false); }
    })();
  }, [targetType, targetId, limit]);

  return (
    <div className="border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] overflow-hidden" data-testid="audit-timeline">
      <div className="px-3 py-2 border-b border-[var(--fop-border)] font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)] flex items-center gap-2">
        <Activity size={12} /> Audit Timeline · {rows.length}
      </div>
      {loading && (
        <div className="px-3 py-4 text-[var(--fop-text-3)] text-xs" data-testid="audit-timeline-loading">Loading…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="px-3 py-4 text-[var(--fop-text-3)] text-xs italic" data-testid="audit-timeline-empty">
          No audited activity yet.
        </div>
      )}
      <ul>
        {rows.map((r) => {
          const Icon = ICON_FOR[r.action] || Activity;
          return (
            <li key={r.id || `${r.action}-${r.created_at}`}
                  className="px-3 py-2 border-b border-[var(--fop-border)] last:border-b-0 flex gap-2"
                  data-testid={`audit-row-${r.action}`}>
              <Icon size={12} className="mt-1 shrink-0 text-[var(--fop-text-3)]" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-mono uppercase tracking-wider text-[var(--fop-text-2)]">
                  {(r.action || "").replace(/\./g, " · ")}
                </div>
                <div className="text-[10px] text-[var(--fop-text-3)] mt-0.5">
                  by {(r.actor_email || r.user_id || r.target_id || "system").slice(0, 40)}
                  {" · "}
                  {(r.created_at || "").slice(0, 19).replace("T", " ")}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
